import { dropdownMenuContentClassName } from '@/shared/components/ui/dropdown-menu';
import { cn } from '@/shared/lib/utils';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import * as React from 'react';

/**
 * RDS Dropdown Menu — Select에 매핑.
 * Context Menu(액션 메뉴)와 달리 트리거+선택 리스트 조합.
 * @see https://www.figma.com/design/wcgji99mZRPIytCZDjwqxe/RDS-v1.0?node-id=2528-4398
 */
export const selectTriggerVariants = cva(
  [
    'flex w-full items-center justify-between gap-3 m-0',
    'font-normal text-ruda-text-primary tracking-tight',
    'transition-[color,background-color,border-color]',
    'focus-visible:outline-none',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'data-[placeholder]:text-ruda-text-tertiary',
    '[&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate [&>span]:text-left',
    // Expand: 보더 #131927, 쉐브론 회전
    'data-[state=open]:border-ruda-text-primary',
    '[&[data-state=open]>svg]:rotate-180',
  ],
  {
    variants: {
      /** Outline = 흰 배경+보더 / Filled = 회색 배경 */
      appearance: {
        outline: [
          'border-[1.5px] border-ruda-border-light bg-white',
          'hover:border-ruda-border-dark',
        ],
        filled: [
          'border-[1.5px] border-transparent bg-ruda-grey-50',
          'hover:bg-ruda-grey-50',
          'data-[state=open]:bg-ruda-grey-50',
        ],
      },
      size: {
        /** Button default(h-9)와 정렬 — 어드민 기본 · radius 12 */
        sm: 'h-9 rounded-12 px-3 py-0 text-sm leading-5',
        md: 'h-auto rounded-16 px-3 py-3 text-sm leading-[22px]',
        lg: 'h-auto rounded-20 px-3 py-4 text-base leading-6',
      },
    },
    defaultVariants: {
      appearance: 'outline',
      size: 'sm',
    },
  },
);

export const selectItemClassName = cn(
  'relative flex w-full cursor-default select-none items-center gap-3',
  'h-12 px-4 text-base font-normal leading-6 tracking-tight outline-none',
  'text-ruda-text-primary transition-colors',
  'data-[highlighted]:bg-ruda-primary-50 data-[highlighted]:text-ruda-text-primary',
  'data-[state=checked]:font-semibold',
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-40',
  '[&>svg]:size-6 [&>svg]:shrink-0',
);

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

type SelectTriggerProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Trigger
> &
  VariantProps<typeof selectTriggerVariants>;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, children, appearance, size, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(selectTriggerVariants({ appearance, size }), className)}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-6 w-6 shrink-0 text-[#6d717f] transition-transform duration-200" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        dropdownMenuContentClassName,
        // 긴 옵션(1일~31일 등) — 최대 높이 제한 후 스크롤
        'max-h-[min(15rem,var(--radix-select-content-available-height))]',
        'min-w-[var(--radix-select-trigger-width)]',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-0 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      sideOffset={8}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'w-full max-h-[inherit] overflow-y-auto',
          position === 'popper' &&
            'min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

type SelectItemProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Item
> & {
  /** false면 체크 자리 없이 텍스트만 (No Icon) */
  showIndicator?: boolean;
};

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  SelectItemProps
>(({ className, children, showIndicator = true, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(selectItemClassName, className)}
    {...props}
  >
    {showIndicator ? (
      <span className="flex size-6 shrink-0 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check
            className="size-5 text-ruda-primary-600"
            strokeWidth={2.5}
          />
        </SelectPrimitive.ItemIndicator>
      </span>
    ) : null}
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className,
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4 text-[#6d717f]" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className,
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4 text-[#6d717f]" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectTrigger,
  SelectValue,
};
